import React from 'react';
import Layout from '../components/layout/Layout';
import { Target, Eye, Heart, Award, Users, BookOpen, Rocket, Globe } from 'lucide-react';
import { aboutContent } from '../data/mock';

const AboutPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-white/10 text-blue-300 text-sm font-semibold rounded-full mb-4 md:mb-6 backdrop-blur-sm border border-white/20">
            About Us
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Pioneering STEAM Education
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">Since 2008</span>
          </h1>
          <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            India&apos;s first credentialing company for STEAM Education, Innovation &amp; Entrepreneurship.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Mission */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-3xl p-8 h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {aboutContent.mission}
                </p>
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-400/20 rounded-full blur-xl"></div>
            </div>

            {/* Vision */}
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-3xl p-8 h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {aboutContent.vision}
                </p>
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-orange-400/20 rounded-full blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
              Our Values
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Drives Us
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {aboutContent.values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-600 to-teal-500 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 text-white font-bold text-sm md:text-base">
                  {index + 1}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm md:text-base">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              What STEAM-IE Stands For
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto text-sm md:text-base">
              Each letter represents our commitment to holistic education.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {[
              { letter: 'S', word: 'Science', desc: 'Understanding the natural world' },
              { letter: 'T', word: 'Technology', desc: 'Tools for innovation' },
              { letter: 'E', word: 'Engineering', desc: 'Building solutions' },
              { letter: 'A', word: 'Arts', desc: 'Creative expression' },
              { letter: 'M', word: 'Mathematics', desc: 'Language of logic' },
              { letter: 'IE', word: 'Innovation & Entrepreneurship', desc: 'Future leaders' }
            ].map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20 text-center hover:bg-white/20 transition-colors duration-300">
                <div className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent mb-1 md:mb-2">
                  {item.letter}
                </div>
                <h3 className="text-white font-semibold text-sm md:text-base mb-1">{item.word}</h3>
                <p className="text-gray-400 text-xs md:text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-600 to-teal-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center text-white">
            <div>
              <div className="text-3xl md:text-5xl font-bold mb-2">17+</div>
              <div className="text-blue-100 text-sm md:text-base">Years of Excellence</div>
            </div>
            <div>
              <div className="text-3xl md:text-5xl font-bold mb-2">2500+</div>
              <div className="text-blue-100 text-sm md:text-base">Students Certified</div>
            </div>
            <div>
              <div className="text-3xl md:text-5xl font-bold mb-2">250+</div>
              <div className="text-blue-100 text-sm md:text-base">Educators Trained</div>
            </div>
            <div>
              <div className="text-3xl md:text-5xl font-bold mb-2">25+</div>
              <div className="text-blue-100 text-sm md:text-base">Partner Schools</div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
